import type {
  PaymentInfoProps as ExtendedPaymentInfoProps,
  SelectInputItem,
  CardErrors,
} from '@openeducation/pp-rn-shared-types';
import { styled, useDimension } from '@openeducation/pp-rn-themes';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { PaymentInfoCountry, PaymentInfoPaymentType } from './fields';
import PaymentMethodOnFileSelector from './payment-method-on-file-selector';
import { usePaymentInfo } from './use-payment-info';
import { usePaymentMethodRenderer } from './use-payment-method-renderer';
import { useTwoCreditCardAmounts } from './use-two-credit-card-amounts';
import { FormSection } from '../../templates';

export const PaymentInfo = ({
  renderCardPreview,
  formData,
  onFieldChange,
  selectData,
  fields,
  validation,
  paymentMethodOnFile,
  paypalEmail,
  withCardStyle,
  totalAmount = 0,
  currency,
  additionalPaymentInfoCard1,
  additionalPaymentInfoCard2,
  mainPaymentMinPercentage,
}: ExtendedPaymentInfoProps) => {
  const { t } = useTranslation();
  const { isCountryEnabled, isCountryFixed, isExistingPaymentMethod } = fields;
  const {
    isEnabled: isPaymentMethodOnFileEnabled,
    ...paymentMethodOnFileProps
  } = paymentMethodOnFile;

  const {
    card1TypeError,
    card1CardNumberError,
    card1ExpirationDateError,
    card1HolderNameError,
    card1CvvError,
    card1AmountError,
    card1ExpiryDate,
    card2TypeError,
    card2CardNumberError,
    card2ExpirationDateError,
    card2HolderNameError,
    card2CvvError,
    card2ExpiryDate,
    handleFocus,
    handleBlur,
    focusedCardFieldType,
  } = usePaymentInfo({ formData, validation, totalAmount });

  const { isMediumSize } = useDimension();

  const { isTwoCreditCards, handleCard1AmountChange, handleCard1AmountBlur } =
    useTwoCreditCardAmounts({
      formData,
      totalAmount,
      currency,
      onFieldChange,
    });

  const cardErrors: CardErrors = {
    card1: {
      type: card1TypeError,
      number: card1CardNumberError,
      expiration: card1ExpirationDateError,
      name: card1HolderNameError,
      cvv: card1CvvError,
      expiryDate: card1ExpiryDate,
    },
    card2: {
      type: card2TypeError,
      number: card2CardNumberError,
      expiration: card2ExpirationDateError,
      name: card2HolderNameError,
      cvv: card2CvvError,
      expiryDate: card2ExpiryDate,
    },
  };

  const { renderPaymentMethodFields, renderCardWithTitle } =
    usePaymentMethodRenderer({
      formData,
      onFieldChange,
      selectData,
      fields,
      renderCardPreview,
      isTwoCreditCards,
      focusedCardFieldType,
      paypalEmail,
      additionalPaymentInfoCard1,
      additionalPaymentInfoCard2,
      currency,
      mainPaymentMinPercentage,
      cardErrors,
      handleFocus,
      handleBlur,
      handleCard1AmountChange,
      handleCard1AmountBlur,
      card1AmountError,
    });

  return (
    <FormSection title={t('title.payment_info')} withCardStyle={withCardStyle}>
      {!isExistingPaymentMethod && isCountryEnabled && (
        <PaymentInfoCountry
          selectedValue={formData.country}
          data={selectData.country as SelectInputItem[]}
          disabled={isCountryFixed}
          onValueChange={(val: string) => onFieldChange(val, 'country')}
          onFocus={() => handleFocus('country')}
          onBlur={handleBlur}
        />
      )}
      <PaymentInfoPaymentType
        selectedValue={formData.paymentMethod}
        data={selectData.paymentMethod as SelectInputItem[]}
        onValueChange={(val: string) => onFieldChange(val, 'paymentMethod')}
        onFocus={() => handleFocus('paymentMethod')}
        onBlur={handleBlur}
      />

      {isPaymentMethodOnFileEnabled && (
        <PaymentMethodOnFileSelector {...paymentMethodOnFileProps} />
      )}

      {isTwoCreditCards ? (
        <CardsRow isMediumSize={isMediumSize}>
          <CardColumn isMediumSize={isMediumSize}>
            {renderCardWithTitle(1)}
          </CardColumn>
          <CardColumn isMediumSize={isMediumSize}>
            {renderCardWithTitle(2)}
          </CardColumn>
        </CardsRow>
      ) : (
        <>{renderPaymentMethodFields(1)}</>
      )}
    </FormSection>
  );
};

export default PaymentInfo;

const CardsRow = styled.View<{ isMediumSize?: boolean }>`
  flex-direction: ${({ isMediumSize }) => (isMediumSize ? 'column' : 'row')};
  gap: 24px;
`;
const CardColumn = styled.View<{ isMediumSize?: boolean }>`
  ${({ isMediumSize }) =>
    !isMediumSize &&
    `
    flex: 1;
  `}
`;

export const CardWrapper = styled.View<{ isTwoCreditCards?: boolean }>`
  width: 100%;
  margin-vertical: ${({ theme }) => theme.spacing.small}px;
  margin-bottom: ${({ theme }) => theme.spacing.medium}px;
`;

export const CardPreviewContainer = styled.View<{ isTwoCreditCards?: boolean }>`
  width: 290px;
  margin-left: ${({ isTwoCreditCards }) => (isTwoCreditCards ? '0' : 'auto')};
  margin-right: ${({ isTwoCreditCards }) => (isTwoCreditCards ? '0' : 'auto')};
`;
